import { useCallback, useMemo } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@cbhq/cds-web/overlays';
import { TextBody, TextLabel1 } from '@cbhq/cds-web/typography';

import { useAppState } from ':cloud/contexts/AppStateContext';
import { useUpdateGasPolicy } from ':cloud/queries/Base/useUpdateGasPolicy';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { GasPolicy, WhitelistedAddressAndMethod } from ':cloud/types/ts_types';

type PaymasterViewAllowlistModalProps = {
  onRequestClose: () => void;
  whitelistItem: WhitelistedAddressAndMethod;
  onRemoveSuccess: (whitelist: WhitelistedAddressAndMethod[]) => void;
  gasPolicy: GasPolicy;
  onEditPress: (address: string) => void;
};
export function PaymasterViewAllowlistModal({
  onRequestClose,
  whitelistItem,
  onRemoveSuccess,
  gasPolicy,
  onEditPress,
}: PaymasterViewAllowlistModalProps) {
  const { activeOrg } = useGetUser();
  const { selectedProject } = useAppState();

  const updateGasPolicy = useUpdateGasPolicy({
    organizationId: activeOrg?.organizationId || '',
    projectId: selectedProject?.id || '',
    onSuccess: (policy: GasPolicy) => {
      onRemoveSuccess?.(policy?.contractAllowlist || []);
    },
    successMsg: 'Updated contract allowlist',
  });

  const whitelistMethods = useMemo(() => {
    if (whitelistItem?.methods?.length) {
      return whitelistItem.methods?.join(', ');
    }
    return 'All functions';
  }, [whitelistItem.methods]);

  const handleRemoveContractPress = useCallback(() => {
    const currentWhitelist = gasPolicy?.contractAllowlist || [];

    // remove item from whitelist
    const updatedWhitelist = currentWhitelist.filter(
      (item) => item.address !== whitelistItem?.address,
    );

    const newGasPolicy = {
      ...gasPolicy,
      contractAllowlist: updatedWhitelist,
    };
    updateGasPolicy.mutate({ policy: newGasPolicy });
    onRequestClose();
  }, [gasPolicy, onRequestClose, updateGasPolicy, whitelistItem?.address]);

  const handleEditContractPress = useCallback(() => {
    onEditPress(whitelistItem.address);
    onRequestClose();
  }, [onEditPress, onRequestClose, whitelistItem.address]);

  return (
    <Modal onRequestClose={onRequestClose} visible>
      <ModalHeader title="Details" />
      <ModalBody>
        <VStack maxWidth={600} spacing={1}>
          <TextLabel1 as="p" spacingBottom={1}>
            Name
          </TextLabel1>
          <TextBody as="p" spacingBottom={4}>
            {whitelistItem?.name || '--'}
          </TextBody>
          <TextLabel1 as="p" spacingBottom={1}>
            Contract Address
          </TextLabel1>
          <TextBody as="p" spacingBottom={4}>
            {whitelistItem.address}
          </TextBody>
          <TextLabel1 as="p" spacingBottom={1}>
            Functions
          </TextLabel1>
          <TextBody as="p">{whitelistMethods}</TextBody>
        </VStack>
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button onPress={handleRemoveContractPress} variant="negative">
            Remove
          </Button>
        }
        secondaryAction={
          <Button onPress={handleEditContractPress} variant="secondary">
            Edit
          </Button>
        }
      />
    </Modal>
  );
}
